import { addDays, endOfMonth, endOfWeek, isWeekend, startOfMonth, startOfWeek } from "date-fns";
import { deLocaleOption, formatIso8601, formatMinutes, localTimezone, parseMinutes } from "@/data/DateTimeUtil";
import { Holiday } from "@/api/Holiday";
import { Timesheet as TimesheetEntry } from "@/api/Timesheet";
import { PendingInhabitant } from "@/api/Pending";
import { ApprovalStatusUtil } from "@/data/ApprovalStatusUtil";

export type CalendarRange = {
  monthStart: Date;
  monthEnd: Date;
  visibleStart: Date;
  visibleEnd: Date;
};

export type TimesheetView = "day" | "week" | "month";

type EntryTimes = {
  startTime: string;
  endTime: string;
};

type DraftEntryOptions = {
  day: Date;
  userId?: number;
  organizationId?: number;
  startTime?: string;
  endTime?: string;
  minutes?: number;
};

export const CALENDAR_COLUMNS = 7;
export const DEFAULT_ENTRY_MINUTES = 8 * 60;

const DEFAULT_START_TIME = "08:00";
const DEFAULT_END_TIME = "17:00";
const MIN_PROPOSED_ENTRY_MINUTES = 30;
const EMPTY_TIMESHEET_ENTRIES: TimesheetEntry[] = [];

export const getCalendarRange = (anchorDate: Date, view: TimesheetView = "month"): CalendarRange => {
  const monthStart = startOfMonth(anchorDate);
  const monthEnd = endOfMonth(monthStart);

  if (view === "day") {
    return {
      monthStart,
      monthEnd,
      visibleStart: anchorDate,
      visibleEnd: anchorDate,
    };
  }

  if (view === "week") {
    return {
      monthStart,
      monthEnd,
      visibleStart: startOfWeek(anchorDate, deLocaleOption),
      visibleEnd: endOfWeek(anchorDate, deLocaleOption),
    };
  }

  return {
    monthStart,
    monthEnd,
    visibleStart: startOfWeek(monthStart, deLocaleOption),
    visibleEnd: endOfWeek(monthEnd, deLocaleOption)
  };
};

export const buildWeekdays = (anchorDate: Date, view: TimesheetView = "month"): Date[] => {
  if (view === "day") {
    return [anchorDate];
  }

  const weekStart = startOfWeek(anchorDate, deLocaleOption);
  return Array.from({ length: CALENDAR_COLUMNS }, (_, index) => addDays(weekStart, index));
};

export const buildCalendarWeeks = (
  { visibleStart, visibleEnd }: CalendarRange,
  view: TimesheetView = "month",
): Date[][] => {
  if (view === "day") {
    return [[visibleStart]];
  }

  const weeks: Date[][] = [];
  let cursor = visibleStart;

  while (cursor <= visibleEnd) {
    const week = Array.from({ length: CALENDAR_COLUMNS }, () => {
      const day = cursor;
      cursor = addDays(cursor, 1);
      return day;
    });
    weeks.push(week);
  }

  return weeks;
};

export const filterWeekendDays = (
  days: Date[],
  view: TimesheetView,
  showWeekends: boolean,
): Date[] => showWeekends || view === "day" ? days : days.filter((day) => !isWeekend(day));

export const groupEntriesByDate = (entries: TimesheetEntry[]): Map<string, TimesheetEntry[]> => {
  const entriesByDate = new Map<string, TimesheetEntry[]>();

  entries.forEach((entry) => {
    const dayEntries = entriesByDate.get(entry.localDate) ?? [];
    dayEntries.push(entry);
    entriesByDate.set(entry.localDate, dayEntries);
  });

  entriesByDate.forEach((dayEntries) => {
    dayEntries.sort((a, b) => a.startTime.localeCompare(b.startTime));
  });

  return entriesByDate;
};

export const getEntriesForDate = (entriesByDate: Map<string, TimesheetEntry[]>, day: Date): TimesheetEntry[] =>
  entriesByDate.get(formatIso8601(day)) ?? EMPTY_TIMESHEET_ENTRIES;

export const mapHolidaysByDate = (holidays: Holiday[]): Map<string, Holiday> =>
  new Map(holidays.map((holiday) => [formatIso8601(holiday.date), holiday]));

export const sumMonthMinutes = (entries: TimesheetEntry[], month: Date): number => {
  const { monthStart, monthEnd } = getCalendarRange(month);
  const firstDay = formatIso8601(monthStart);
  const lastDay = formatIso8601(monthEnd);

  return entries
    .filter((entry) => entry.localDate >= firstDay && entry.localDate <= lastDay)
    .filter((entry) => ApprovalStatusUtil.isPending(entry.status) || ApprovalStatusUtil.isApproved(entry.status))
    .reduce((sum, entry) => sum + entry.minutes, 0);
};

export const proposeEntryTimes = (requiredMinutes: number, dailyEntries: TimesheetEntry[]): EntryTimes => {
  if (dailyEntries.length === 0) {
    return {
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME
    };
  }

  const existingMinutes = dailyEntries.reduce((sum, entry) => sum + entry.minutes, 0);
  const startTime = dailyEntries[dailyEntries.length - 1].endTime;
  const proposedMinutes = Math.max(requiredMinutes - existingMinutes, MIN_PROPOSED_ENTRY_MINUTES);

  return {
    startTime,
    endTime: formatMinutes(parseMinutes(startTime) + proposedMinutes)
  };
};

export const createDraftEntry = ({
  day,
  userId,
  organizationId,
  startTime = "",
  endTime = "",
  minutes = 0
}: DraftEntryOptions): TimesheetEntry => ({
  id: -1,
  localDate: formatIso8601(day),
  timezone: localTimezone,
  startTime,
  endTime,
  minutes,
  entryId: undefined,
  projectId: undefined,
  status: PendingInhabitant,
  comments: undefined,
  userId,
  organizationId,
  decidedBy: null,
  decidedAt: null,
  decisionReason: null
});
